import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { inviteToProject } from "./api";

const PROJECT_ROLES = [
  { value: "PROJECT_ADMIN", label: "Workspace Admin" },
  { value: "CONTRIBUTOR", label: "Contributor" },
  { value: "VIEWER", label: "Viewer" },
];

const isEmailish = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

/**
 * SCK-476 — single-click "invite this person into THIS workspace" flow.
 *
 * Console equivalent of `c2a user invite -e ... -P <project> -r PROJECT_ADMIN`.
 * The user picks a workspace and role in one dialog rather than the
 * two-step (invite at account, then grant per project) dance today.
 *
 * Contract: POST is issued to `/accounts/{project.accountId}/members`
 * (the project's OWNING account, not the caller's — matches SCK-475
 * CLI fix). See api.inviteToProject for the payload shape.
 *
 * Props:
 *   open        boolean
 *   projects    Project[] — from /k8s-project/; each must carry accountId
 *   defaultProjectId? string — pre-select when opened from a project ctx
 *   onClose()   close without action
 *   onInvited({ email, projectRole, projectName, invitation }) —
 *              success callback. `invitation` is the coord SCK-617
 *              response block: `{id, redemptionUrl, deliveryMode,
 *              expires_on, ...}` when the token subsystem is active,
 *              or `null` for legacy mint paths. The parent decides
 *              whether to surface the copy-link modal or an
 *              email-sent toast based on `invitation.deliveryMode`.
 */
export default function InviteToProjectDialog({
  open,
  projects,
  defaultProjectId,
  onClose,
  onInvited,
}) {
  const invitable = useMemo(
    () => (projects || []).filter((p) => !!p?.accountId),
    [projects]
  );

  const [projectId, setProjectId] = useState(
    defaultProjectId && invitable.some((p) => p.id === defaultProjectId)
      ? defaultProjectId
      : invitable[0]?.id || ""
  );
  const [email, setEmail] = useState("");
  const [projectRole, setProjectRole] = useState("CONTRIBUTOR");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const project = invitable.find((p) => p.id === projectId) || null;
  const missingOwners = (projects || []).length - invitable.length;
  const canSubmit =
    !submitting && !!project && isEmailish(email) && !!projectRole;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await inviteToProject({
        project,
        email: email.trim(),
        projectRole,
      });
      // SCK-619 — coord SCK-617 now returns `{invitation, member}`.
      // Legacy responses (or a raw member row) may not carry
      // `.invitation`; fall through with `null` so the parent renders
      // the plain "invited" toast without a copy-link modal.
      const invitation =
        response && typeof response === "object" && response.invitation
          ? response.invitation
          : null;
      onInvited({
        email: email.trim(),
        projectRole,
        projectName: project.name,
        invitation,
      });
    } catch (e) {
      const status = e?.status;
      let msg;
      if (status === 403) {
        msg =
          "You don't have permission to invite into this workspace. Ask a Workspace Admin or Account Admin.";
      } else if (status === 400) {
        // Coord surfaces "already a member" / invalid email as 400 — pass
        // the trimmed backend message through so the user sees the reason.
        const bodyMsg =
          (e?.body && e.body.slice(0, 200)) || "Check the email and try again.";
        msg = `Couldn't send invite: ${bodyMsg}`;
      } else {
        msg = e?.message || "Couldn't send invite.";
      }
      setError({ status, message: msg });
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Invite to a project</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            One-step invite into a single workspace. Grants only the role
            you pick below — no account-level admin.
          </Typography>

          {invitable.length === 0 ? (
            <Alert severity="warning">
              No workspaces are eligible for direct invites. Ask an Account
              Admin to create one first.
            </Alert>
          ) : (
            <>
              <Autocomplete
                options={invitable}
                value={project}
                onChange={(_, v) => setProjectId(v?.id || "")}
                getOptionLabel={(p) => p?.name || ""}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                disabled={submitting}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Workspace"
                    required
                    inputProps={{
                      ...params.inputProps,
                      "aria-label": "Workspace",
                    }}
                  />
                )}
              />
              {missingOwners > 0 && (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {missingOwners} workspace{missingOwners === 1 ? "" : "s"} hidden
                  (no owning account exposed by the API yet).
                </Typography>
              )}

              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                error={!!email && !isEmailish(email)}
                helperText={
                  !!email && !isEmailish(email) ? "Enter a valid email address." : " "
                }
                inputProps={{ "aria-label": "Email" }}
              />

              <FormControl fullWidth disabled={submitting}>
                <InputLabel id="invite-project-role-label">
                  Workspace role
                </InputLabel>
                <Select
                  labelId="invite-project-role-label"
                  label="Workspace role"
                  value={projectRole}
                  onChange={(e) => setProjectRole(e.target.value)}
                  inputProps={{ "aria-label": "Workspace role" }}
                >
                  {PROJECT_ROLES.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ color: "text.secondary", fontSize: 13 }}>
                Sent as MEMBER at the account level; workspace access is scoped
                to <strong>{project?.name || "—"}</strong>.
              </Box>
            </>
          )}

          {error && (
            <Alert severity={error.status === 403 ? "warning" : "error"}>
              {error.message}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? "Sending…" : "Send invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
